import React, { Suspense, useState, useEffect } from 'react'
import { useParams } from 'react-router'
import { NiceTabPanel } from '../../components/TabsUI/TabsUI'
import ShipmentShowHomeContent, { shipmentHomeQuery } from './ShipmentShowHomeContent'
import ShipmentShowHeroCard from './ShipmentShowHeroCard'
import NoteContent from '../../components/NoteTab'
import ShipmentEdit from './ShipmentEdit'
import LiveShareTab from '../../components/LiveShareTab'
import ShipmentFolders from './ShipmentFolders'
import ShipmentEmails from './ShipmentEmails'
import ShipmentFinances, { shipmentFinanceQuery } from './ShipmentFinances'
import { fetchQuery, useRelayEnvironment } from 'react-relay/hooks'
import { ShipmentFinancesQuery } from '../../__generated__/ShipmentFinancesQuery.graphql'
import { getRequest, createOperationDescriptor } from 'relay-runtime'
import styled from 'styled-components'
import EntityShowOrchestrator from '../../components/EntityShowOrchestrator'
import { uploadableDocsQuery } from '../../components/File/helpers'
import { ShowPageRenderContext } from '../../viewTypes'
import { ShipmentLinkedContactsTab } from '../../components/LinkedContactsTab'

const ShipmentShow: React.FC = () => {
  const { slug, tab } = useParams()
  return <ShipmentShowWithParams slug={slug} tab={tab} renderContext='page' />
}

interface Params {
  slug: string
  tab: string
  withSkeleton?: boolean
  renderContext?: ShowPageRenderContext
}

const TabPanelsContainer = styled.div`
  min-height: 90vh;
`

export const ShipmentShowWithParams: React.FC<Params> = ({ slug, tab, withSkeleton = false, renderContext = 'drawer' }) => {
  const [viewMode, setViewMode] = useState<'show' | 'edit'>('show')
  const environment = useRelayEnvironment()

  useEffect(() => {
    // explicitly preloading finance on initial render
    // TODO: switch to `useQueryLoader` and proper preloaded queries once they are usable
    fetchQuery<ShipmentFinancesQuery>(environment, shipmentFinanceQuery, { slug: slug }).toPromise()

    // telling relay to retain these queries in store even when their components are not mounted (until this component unmounts)
    const financeQueryDisposable = environment.retain(createOperationDescriptor(getRequest(shipmentFinanceQuery), { slug: slug }))
    const homeQueryDisposable = environment.retain(createOperationDescriptor(getRequest(shipmentHomeQuery), { slug: slug }))
    const uploadableDocsQueryDisposable = environment.retain(
      createOperationDescriptor(getRequest(uploadableDocsQuery), { entityType: 'shipments' })
    )

    return () => {
      financeQueryDisposable.dispose()
      homeQueryDisposable.dispose()
      uploadableDocsQueryDisposable.dispose()
    }
  }, [slug, environment])

  return (
    <EntityShowOrchestrator
      keyId={slug}
      showPage={
        <>
          {withSkeleton ? (
            <Suspense fallback={<ShipmentShowHeroCard.Skeleton />}>
              <ShipmentShowHeroCard slug={slug} activeTab={tab} onEdit={() => setViewMode('edit')} renderContext={renderContext} />
            </Suspense>
          ) : (
            <ShipmentShowHeroCard slug={slug} activeTab={tab} onEdit={() => setViewMode('edit')} renderContext={renderContext} />
          )}

          <TabPanelsContainer>
            <NiceTabPanel forTab='home' activeTab={tab}>
              <Suspense fallback={<ShipmentShowHomeContent.Skeleton />}>
                <ShipmentShowHomeContent slug={slug} />
              </Suspense>
            </NiceTabPanel>

            <NiceTabPanel forTab='files' activeTab={tab}>
              <Suspense fallback={<ShipmentFolders.Skeleton />}>
                <ShipmentFolders slug={slug} />
              </Suspense>
            </NiceTabPanel>

            <NiceTabPanel forTab='contacts' activeTab={tab}>
              <Suspense fallback={<ShipmentLinkedContactsTab.Skeleton />}>
                <ShipmentLinkedContactsTab slug={slug} />
              </Suspense>
            </NiceTabPanel>

            <NiceTabPanel forTab='emails' activeTab={tab}>
              <Suspense fallback={<ShipmentEmails.Skeleton />}>
                <ShipmentEmails slug={slug} />
              </Suspense>
            </NiceTabPanel>

            <NiceTabPanel forTab='shares' activeTab={tab}>
              <Suspense fallback={<LiveShareTab.Skeleton />}>
                <LiveShareTab entitySlug={slug} />
              </Suspense>
            </NiceTabPanel>

            <NiceTabPanel forTab='finances' activeTab={tab}>
              <Suspense fallback={<ShipmentFinances.Skeleton />}>
                <ShipmentFinances slug={slug} />
              </Suspense>
            </NiceTabPanel>

            <NiceTabPanel forTab='notes' activeTab={tab}>
              <Suspense fallback={<NoteContent.Skeleton />}>
                <NoteContent slug={slug} entityType='shipments' />
              </Suspense>
            </NiceTabPanel>
          </TabPanelsContainer>
        </>
      }
      editPage={<ShipmentEdit slug={slug} onClose={() => setViewMode('show')} />}
      viewMode={viewMode}
    />
  )
}

export default ShipmentShow
